import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import { Button, Card, CardFooter, FormGroup, Input, Label, Row } from 'reactstrap';
import BreadCrumbs from '../../layouts/breadcrumbs/BreadCrumbs';
import ComponentCard from "../ComponentCard";
import useHttp from "../../hooks/useHttp";
import Loader from "../../layouts/loader/Loader";
import useAlert from "../../hooks/useAlert";
import useInput from "../../hooks/useInput";
import { checkEmpty, checkGtZero } from "../../utils/utilFunctions";

const PackageDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { value: name, touched: isTouchedName, isValid: isValidName, valueChangeHandler: nameChangeHandler, explicitSet: explicitSetName } = useInput('', checkEmpty);
    const { value: code, touched: isTouchedCode, isValid: isValidCode, valueChangeHandler: codeChangeHandler, explicitSet: explicitSetCode } = useInput('', checkEmpty);
    const { value: fee, touched: isTouchedFee, isValid: isValidFee, valueChangeHandler: feeChangeHandler, explicitSet: explicitSetFee } = useInput(0, checkGtZero);
    const { value: status, touched: isTouchedStatus, isValid: isValidStatus, valueChangeHandler: statusChangeHandler, explicitSet: explicitSetStatus } = useInput('', checkEmpty);
    const { value: packageTypeCode, touched: isTouchedPackageTypeCode, isValid: isValidPackageTypeCode, valueChangeHandler: packageTypeCodeChangeHandler, explicitSet: explicitSetPackageTypeCode } = useInput('', checkEmpty);
    const { value: bandwidthDisplay, touched: isTouchedBandwidthDisplay, isValid: isValidBandwidthDisplay, valueChangeHandler: bandwidthDisplayChangeHandler, explicitSet: explicitSetBandwidthDisplay } = useInput('', checkEmpty);
    const { value: dataDisplay, touched: isTouchedDataDisplay, isValid: isValidDataDisplay, valueChangeHandler: dataDisplayChangeHandler, explicitSet: explicitSetDataDisplay } = useInput('', checkEmpty);
    const { value: validityDisplay, touched: isTouchedValidityDisplay, isValid: isValidValidityDisplay, valueChangeHandler: validityDisplayChangeHandler, explicitSet: explicitSetValidityDisplay } = useInput('', checkEmpty);
    const { value: installationFee, valueChangeHandler: installationFeeChangeHandler, explicitSet: explicitSetInstallationFee } = useInput(0, value => !Number.isNaN(value));
    const { value: customerForceFee, valueChangeHandler: customerForceFeeChangeHandler, explicitSet: explicitSetCustomerForceFee } = useInput(false, value => value === 'true');
    const { value: trafficLimit, valueChangeHandler: trafficLimitChangeHandler, explicitSet: explicitSetTrafficLimit } = useInput(0, value => !Number.isNaN(value));
    const { value: trafficLimitAction, valueChangeHandler: trafficLimitActionChangeHandler, explicitSet: explicitSetTrafficLimitAction } = useInput('', checkEmpty);
    const { value: publicIpFee, valueChangeHandler: publicIpFeeChangeHandler, explicitSet: explicitSetPublicIpFee } = useInput(0, value => !Number.isNaN(value));
    const { value: privateIpFee, valueChangeHandler: privateIpFeeChangeHandler, explicitSet: explicitSetPrivateIpFee } = useInput(0, value => !Number.isNaN(value));
    const { value: bonusDay, valueChangeHandler: bonusDayChangeHandler, explicitSet: explicitSetBonusDay } = useInput(false, value => value === 'true');
    const { value: bonusDayDays, valueChangeHandler: bonusDayDaysChangeHandler, explicitSet: explicitSetBonusDayDays } = useInput(0, value => !Number.isNaN(value));
    const { value: bonusDayPack, valueChangeHandler: bonusDayPackChangeHandler, explicitSet: explicitSetBonusDayPack } = useInput(false, value => value === 'true');
    const { value: servicePeriodQuantity, valueChangeHandler: servicePeriodQuantityChangeHandler, explicitSet: explicitSetServicePeriodQuantity } = useInput(0, value => !Number.isNaN(value));
    const { value: servicePeriodUnit, valueChangeHandler: servicePeriodUnitChangeHandler, explicitSet: explicitSetServicePeriodUnit } = useInput(0, value => !Number.isNaN(value));
    const { value: note, valueChangeHandler: noteChangeHandler, explicitSet: explicitSetNote } = useInput('', checkEmpty);
    const [statusOptions, setStatusOptions] = useState([]);
    const [packageTypeOptions, setPackageTypeOptions] = useState([]);
    const { error, isLoading, sendRequest } = useHttp();
    const { alert, successAlert, errorAlert } = useAlert();

    useEffect(() => error ? errorAlert(error) : null, [error]);

    const loadPackageDetails = data => {
        explicitSetName(data.name);
        explicitSetCode(data.code);
        explicitSetFee(data.fee);
        explicitSetStatus(data.status);
        explicitSetPackageTypeCode(data.packageTypeCode);
        explicitSetBandwidthDisplay(data.bandwidthDisplay);
        explicitSetDataDisplay(data.dataDisplay);
        explicitSetValidityDisplay(data.validityDisplay);
        explicitSetInstallationFee(data.installationFee);
        explicitSetCustomerForceFee(data.customerForceFee);
        explicitSetTrafficLimit(data.trafficLimit);
        explicitSetTrafficLimitAction(data.trafficLimitAction);
        explicitSetPublicIpFee(data.publicIpFee);
        explicitSetPrivateIpFee(data.privateIpFee);
        explicitSetBonusDay(data.bonusDay);
        explicitSetBonusDayDays(data.bonusDayDays);
        explicitSetBonusDayPack(data.bonusDayPack);
        explicitSetServicePeriodQuantity(data.servicePeriodQuantity);
        explicitSetServicePeriodUnit(data.servicePeriodUnit);
        explicitSetNote(data.note);
    }

    useEffect(() => {
        if (id) {
            sendRequest({
                url: `${process.env.REACT_APP_IBILL_GATEWAY}/platform/api/v1/packages/${id}`,
                headers: {
                    'Accept': 'application/json'
                }
            }, loadPackageDetails);
        }
    }, [id]);

    useEffect(() => {

        setStatusOptions(["Active", "InActive"]);

        sendRequest({
            url: `${process.env.REACT_APP_IBILL_GATEWAY}/platform/api/v1/package-types`,
            headers: {
                'Accept': 'application/json'
            }
        }, data => setPackageTypeOptions(data));
    }, []);

    const cancelAction = () => navigate("../list");
    const successAction = (data) => successAlert(`Successfully ${id ? "updated" : "created"} Package : ${data.name}`, () => navigate("../list"));

    const submitAction = async () => {
        const isValid = isValidName && isValidCode && isValidFee && isValidPackageTypeCode && isValidBandwidthDisplay && isValidDataDisplay && isValidValidityDisplay;
        if (!isValid) {
            errorAlert(`All mandatory fields are required`);
            return;
        }
        const payload = { id, name, code, fee, status, packageTypeCode, bandwidthDisplay, dataDisplay, validityDisplay, installationFee, customerForceFee, trafficLimit, trafficLimitAction, publicIpFee, privateIpFee, bonusDay, bonusDayDays, bonusDayPack, servicePeriodQuantity, servicePeriodUnit, note };
        const requestConfigData = {
            url: `${process.env.REACT_APP_IBILL_GATEWAY}/platform/api/v1/package`,
            method: id ? "PUT" : "POST",
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: payload,
        };
        sendRequest(requestConfigData, successAction)
    }

    return (
      <div>
          {alert}
          <BreadCrumbs />
          {isLoading
            ? <Loader />
            : <>
                <ComponentCard title="Package Basic Details">
                    <Row>
                        <FormGroup className="col-md-4">
                            <Label>Name </Label>
                            <Input
                              type="text"
                              value={name}
                              placeholder="Name"
                              onChange={nameChangeHandler}
                              onBlur={nameChangeHandler}
                              disabled
                            />
                            {!isValidName && isTouchedName ? (
                              <span className="text-danger">Name Required</span>) : null}
                        </FormGroup>
                        <FormGroup className="col-md-4">
                            <Label>Code </Label>
                            <Input
                              type="text"
                              value={code}
                              placeholder="Code"
                              onChange={codeChangeHandler}
                              onBlur={codeChangeHandler}
                              disabled
                            />
                            {!isValidCode && isTouchedCode ? (
                              <span className="text-danger">Code is invalid</span>) : null}
                        </FormGroup>
                        <FormGroup className="col-md-4">
                            <Label>Fee </Label>
                            <Input
                              type="number"
                              value={fee}
                              placeholder="Fee"
                              onChange={feeChangeHandler}
                              onBlur={feeChangeHandler}
                              disabled
                            />
                            {!isValidFee && isTouchedFee ? (
                              <span className="text-danger">Fee is invalid</span>) : null}
                        </FormGroup>
                        <FormGroup className="col-md-4">
                            <Label>Status </Label>
                            <Input type="select"
                                   value={status}
                                   onChange={statusChangeHandler}
                                   onBlur={statusChangeHandler}
                            >
                                <option value="-1">&lt;&nbsp;Select&nbsp;&gt;</option>
                                {statusOptions.map(option => (
                                  <option key={option} value={option}>{option}</option>
                                ))}
                            </Input>
                            {!isValidStatus && isTouchedStatus ? (
                              <span className="text-danger">Status is invalid</span>) : null}
                        </FormGroup>
                        <FormGroup className="col-md-4">
                            <Label>Package Type Code </Label>
                            <Input type="select"
                                   value={packageTypeCode}
                                   onChange={packageTypeCodeChangeHandler}
                                   onBlur={packageTypeCodeChangeHandler}
                            >
                                <option value="-1">&lt;&nbsp;Select&nbsp;&gt;</option>
                                {packageTypeOptions.map(option => (
                                  <option key={option.code} value={option.code}>{option.code}</option>
                                ))}
                            </Input>
                            {!isValidPackageTypeCode && isTouchedPackageTypeCode ? (
                              <span className="text-danger">Package Type Code is invalid</span>) : null}
                        </FormGroup>
                        <FormGroup className="col-md-4">
                            <Label>Bandwidth Display </Label>
                            <Input
                              type="text"
                              value={bandwidthDisplay}
                              placeholder="Bandwidth Display"
                              onChange={bandwidthDisplayChangeHandler}
                              onBlur={bandwidthDisplayChangeHandler}
                            />
                            {!isValidBandwidthDisplay && isTouchedBandwidthDisplay ? (
                              <span className="text-danger">Bandwidth Display is invalid</span>) : null}
                        </FormGroup>
                        <FormGroup className="col-md-4">
                            <Label>Data Display </Label>
                            <Input
                              type="text"
                              value={dataDisplay}
                              placeholder="Data Display"
                              onChange={dataDisplayChangeHandler}
                              onBlur={dataDisplayChangeHandler}
                            />
                            {!isValidDataDisplay && isTouchedDataDisplay ? (
                              <span className="text-danger">Data Display is invalid</span>) : null}
                        </FormGroup>
                        <FormGroup className="col-md-4">
                            <Label>Validity Display </Label>
                            <Input
                              type="text"
                              value={validityDisplay}
                              placeholder="Validity Display"
                              onChange={validityDisplayChangeHandler}
                              onBlur={validityDisplayChangeHandler}
                            />
                            {!isValidValidityDisplay && isTouchedValidityDisplay ? (
                              <span className="text-danger">Validity Display is invalid</span>) : null}
                        </FormGroup>
                    </Row>
                </ComponentCard>

                <ComponentCard title="Package Advance Details">
                    <Row>
                        <FormGroup className="col-md-4">
                            <Label>Installation Fee </Label>
                            <Input
                              type="number"
                              value={installationFee}
                              placeholder="Installation Fee"
                              onChange={installationFeeChangeHandler}
                              onBlur={installationFeeChangeHandler}
                            />
                        </FormGroup>
                        <FormGroup className="col-md-4">
                            <Label>Public IP Fee </Label>
                            <Input
                              type="number"
                              value={publicIpFee}
                              placeholder="Public IP Fee"
                              onChange={publicIpFeeChangeHandler}
                              onBlur={publicIpFeeChangeHandler}
                            />
                        </FormGroup>
                        <FormGroup className="col-md-4">
                            <Label>Private IP Fee </Label>
                            <Input
                              type="number"
                              value={privateIpFee}
                              placeholder="Private IP Fee"
                              onChange={privateIpFeeChangeHandler}
                              onBlur={privateIpFeeChangeHandler}
                            />
                        </FormGroup>
                        <FormGroup className="col-md-4">
                            <Label>Traffic Limit </Label>
                            <Input
                              type="number"
                              value={trafficLimit}
                              placeholder="Traffic Limit"
                              onChange={trafficLimitChangeHandler}
                              onBlur={trafficLimitChangeHandler}
                            />
                        </FormGroup>
                        <FormGroup className="col-md-4">
                            <Label>Bonus Day Days </Label>
                            <Input
                              type="number"
                              value={bonusDayDays}
                              placeholder="Bonus Day Days"
                              onChange={bonusDayDaysChangeHandler}
                              onBlur={bonusDayDaysChangeHandler}
                            />
                        </FormGroup>
                        <FormGroup className="col-md-4">
                            <Label>Traffic Limit Action </Label>
                            <Input
                              type="text"
                              value={trafficLimitAction}
                              placeholder="Traffic Limit Action"
                              onChange={trafficLimitActionChangeHandler}
                              onBlur={trafficLimitActionChangeHandler}
                            />
                        </FormGroup>
                        <FormGroup className="col-md-4">
                            <Label>Customer Force Fee </Label>
                            <Input
                              type="select"
                              value={customerForceFee}
                              onChange={customerForceFeeChangeHandler}
                              onBlur={customerForceFeeChangeHandler}
                            >
                                <option value="true">True</option>
                                <option value="false">False</option>
                            </Input>
                        </FormGroup>
                        <FormGroup className="col-md-4">
                            <Label>Bonus Day </Label>
                            <Input
                              type="select"
                              value={bonusDay}
                              onChange={bonusDayChangeHandler}
                              onBlur={bonusDayChangeHandler}
                            >
                                <option value="true">True</option>
                                <option value="false">False</option>
                            </Input>
                        </FormGroup>
                        <FormGroup className="col-md-4">
                            <Label>Bonus Day Pack </Label>
                            <Input
                              type="select"
                              value={bonusDayPack}
                              onChange={bonusDayPackChangeHandler}
                              onBlur={bonusDayPackChangeHandler}
                            >
                                <option value="true">True</option>
                                <option value="false">False</option>
                            </Input>
                        </FormGroup>
                        <FormGroup className="col-md-4">
                            <Label>Service Period Quantity </Label>
                            <Input
                              type="number"
                              value={servicePeriodQuantity}
                              placeholder="Service Period Quantity"
                              onChange={servicePeriodQuantityChangeHandler}
                              onBlur={servicePeriodQuantityChangeHandler}
                            />
                        </FormGroup>
                        <FormGroup className="col-md-4">
                            <Label>Service Period Unit </Label>
                            <Input
                              type="select"
                              value={servicePeriodUnit}
                              onChange={servicePeriodUnitChangeHandler}
                              onBlur={servicePeriodUnitChangeHandler}
                            >
                                <option value="day">Day</option>
                                <option value="month">Month</option>
                            </Input>
                        </FormGroup>
                        <FormGroup className="col-md-4">
                            <Label>Note </Label>
                            <Input
                              type="textarea"
                              value={note}
                              placeholder="Note"
                              onChange={noteChangeHandler}
                              onBlur={noteChangeHandler}
                            />
                        </FormGroup>
                    </Row>
                </ComponentCard>

                <Card>
                    <CardFooter>
                        <Row>
                            <div className="col-md-2">
                                <Button className="btn col-12 btn-block btn-danger"
                                        onClick={cancelAction}>Cancel</Button>
                            </div>
                            <div className="col-md-8"/>
                            <div className="col-md-2">
                                <Button className="btn col-12 btn-block btn-success"
                                        onClick={submitAction}>Submit</Button>
                            </div>
                        </Row>
                    </CardFooter>
                </Card>
            </>}
      </div>
    );
};

export default PackageDetails;